import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { AppUser, PreauthSettings } from '../types/auth';
import { redirectToLogin, redirectToLogout, deferRedirect, resolveCitationHref } from '../utils/backendUrl';
import { networkEventManager } from '../services/networkEventManager';
import { authService } from '../services/authService';
import { mainStore } from '../stores/MainStore';
import { wipeTenantFilters } from '../components/helpers/filtersTenantStorage';

// Module-level guard to prevent duplicate session fetching (survives React StrictMode double-invoke)
let globalAuthInitDone = false;

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  loggingOut: boolean;
  pendingApproval: boolean; // User account not yet approved by administrator
  pendingApprovalMessage: string | null; // Backend message when pending approval
  pendingApprovalSuggestion: string | null; // Backend suggestion (e.g. details.suggestion)
  isAdmin: boolean; // User is in admin Cognito group
  signature: string | null;
  is_external: boolean; // User is in the preauth/external api
  preauthSettings: PreauthSettings | null; // Feature/nav restrictions for preauth sessions
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateSignature: (signature: string) => Promise<boolean>;
  setPendingApprovalDetails: (message: string | null, suggestion: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const DEFAULT_PAGE_TITLE = 'Reve - RCM AI Assistant';
export const DEFAULT_ASK_REVE_LABEL = 'Ask Reve';
export const WHITELABEL_PAGE_TITLE = 'Claim Assistant';
export const WHITELABEL_ASK_REVE_LABEL = 'Ask Claim Assistant';

export interface OrgLinkBranding {
  pageTitle: string;
  askReveLabel: string;
  logoUrl: string | null;
  whitelabel: boolean;
}

export const useOrgLinkBranding = (): OrgLinkBranding => {
  const ctx = useContext(AuthContext);
  const org = ctx?.user?.backend?.current_tenant?.organization;
  const whitelabel = Boolean(org?.whitelabel);
  const branded = Boolean(ctx?.is_external) || whitelabel;
  const pageTitle = (branded && org?.page_title?.trim())
    || (whitelabel ? WHITELABEL_PAGE_TITLE : DEFAULT_PAGE_TITLE);
  const askReveLabel = (branded && org?.ask_reve_button_text?.trim())
    || (whitelabel ? WHITELABEL_ASK_REVE_LABEL : DEFAULT_ASK_REVE_LABEL);
  const logoUrl = (branded && resolveCitationHref(org?.logo_url ?? undefined)) || null;
  return { pageTitle, askReveLabel, logoUrl, whitelabel };
};


// Saved filters are per tenant and user (and encrypted), so the stores must have that user's
// decrypted filters before any page renders: await this before setUser. Returns the user
// without the filters encryption key, which stays out of what every useAuth() consumer sees.
const applySessionFilters = async (sessionUser: AppUser | null | undefined): Promise<AppUser | null> => {
  const backend = sessionUser?.backend;
  await mainStore.setFiltersScope(
    backend?.current_tenant?.id,
    sessionUser?.username || sessionUser?.email,
    backend?.filters_encryption_key,
    backend?.filters_key_id,
  );
  if (!sessionUser || !backend) return sessionUser ?? null;
  const { filters_encryption_key: _key, ...rest } = backend;
  return { ...sessionUser, backend: rest };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [pendingApprovalMessage, setPendingApprovalMessage] = useState<string | null>(null);
  const [pendingApprovalSuggestion, setPendingApprovalSuggestion] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [is_external, setIsExternal] = useState<boolean>(false);
  const [preauthSettings, setPreauthSettings] = useState<PreauthSettings | null>(null);

  useEffect(() => {
    // Use module-level guard (survives React StrictMode double-invoke)
    if (globalAuthInitDone) {
      return;
    }
    globalAuthInitDone = true;
    
    (async () => {
      try {
        // Try to get session from backend using AuthService
        const sessionResult = await authService.getSession();

        // Check if user account is pending approval
        if (sessionResult.pendingApproval) {
          setPendingApproval(true);
          setPendingApprovalMessage(sessionResult.message ?? null);
          setPendingApprovalSuggestion(sessionResult.suggestion ?? null);
          setUser(null);
          setIsAdmin(false);
          setSignature(null);
          setIsExternal(false);
          setPreauthSettings(null);
        } else {
          setPendingApproval(false);
          setPendingApprovalMessage(null);
          setPendingApprovalSuggestion(null);
          setUser(await applySessionFilters(sessionResult.user));

          setIsAdmin(Boolean(sessionResult.user?.backend?.is_admin));

          // Extract signature from session response
          const userSignature = sessionResult.user?.backend?.signature;
          setSignature(userSignature || null);

          setIsExternal(Boolean(sessionResult.user?.backend?.is_external));
          setPreauthSettings(sessionResult.user?.backend?.preauth_settings ?? null);
        }
      } catch (error) {
        setUser(null);
        setPendingApproval(false);
        setPendingApprovalMessage(null);
        setPendingApprovalSuggestion(null);
        setIsAdmin(false);
        setSignature(null);
        setIsExternal(false);
        setPreauthSettings(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const userRef = useRef<AppUser | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);

  // Listen for auth failure events from other parts of the app (e.g., reconnection popup, token refresh)
  useEffect(() => {
    const handleAuthFailure = () => {
      void mainStore.setFiltersScope(null, null);
      setUser(null);
      redirectToLogin();
    };

    networkEventManager.on('auth:failure', handleAuthFailure);

    return () => {
      networkEventManager.off('auth:failure', handleAuthFailure);
    };
  }, []);



  const signOut = async () => {
    setLoggingOut(true);
    try {
      // An explicit sign-out removes the saved filters entry (a refresh keeps it); the
      // backend rotates the key on logout too, which would leave it unreadable anyway.
      try {
        await wipeTenantFilters();
      } catch {
        // Never block the logout on it
      }
      await mainStore.setFiltersScope(null, null);
      setUser(null);
      redirectToLogout();
    } catch (error) {
      setUser(null);
      setLoggingOut(false);
      deferRedirect('/');
    }
  };

  const refreshSession = async () => {
    try {
      const sessionResult = await authService.refreshSession();
      setUser(await applySessionFilters(sessionResult.user));

      setIsAdmin(Boolean(sessionResult.user?.backend?.is_admin));

      // Update signature from session response
      const userSignature = sessionResult.user?.backend?.signature;
      setSignature(userSignature || null);

      setIsExternal(Boolean(sessionResult.user?.backend?.is_external));
      setPreauthSettings(sessionResult.user?.backend?.preauth_settings ?? null);
    } catch (error) {
      // No session, so no scope: stop reading/writing the previous user's saved filters.
      void mainStore.setFiltersScope(null, null);
      setUser(null);
      setIsAdmin(false);
      setSignature(null);
      setIsExternal(false);
      setPreauthSettings(null);
    }
  };

  const setPendingApprovalDetails = (msg: string | null, sugg: string | null) => {
    setPendingApprovalMessage(msg);
    setPendingApprovalSuggestion(sugg);
  };

  const updateSignature = async (newSignature: string): Promise<boolean> => {
    try {
      const result = await authService.updateSignature(newSignature);

      if (result.success) {
        const updatedSignature = result.signature || newSignature;
        setSignature(updatedSignature);
        
        // Also update user object if it exists
        if (user && user.backend) {
          setUser({
            ...user,
            backend: {
              ...user.backend,
              signature: updatedSignature
            }
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update signature:', error);
      return false;
    }
  };

  const value: AuthContextType = { user, loading, loggingOut, pendingApproval, pendingApprovalMessage, pendingApprovalSuggestion, isAdmin, signature, is_external, preauthSettings, signOut, refreshSession, updateSignature, setPendingApprovalDetails };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


